import React from "react";
import { motion } from "framer-motion";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

function ContentLine({ line }) {
  // Prominent heading: lines prefixed with "## "
  if (line.startsWith("## ")) {
    return (
      <p className="font-display text-2xl md:text-3xl text-foreground pt-6 pb-1">
        {line.slice(3)}
      </p>
    );
  }
  // Small accent sub-heading: short ALL-CAPS lines
  const isHeading = line === line.toUpperCase() && line.length < 48 && /[A-Z]/.test(line);
  if (isHeading) {
    return <p className="subhead text-accent text-base md:text-lg pt-5 pb-0.5">{line}</p>;
  }
  // Bold a leading ALL-CAPS label followed by a dash (bullet optional, em dash or spaced hyphen)
  const m = line.match(/^(\s*•?\s*)([A-Z0-9][A-Z0-9 '’&/()]*[A-Z0-9)])(?:\s*[—–]\s*|\s+-\s+)([\s\S]+)$/);
  const isBullet = line.trim().startsWith("•");
  if (m && m[2].trim().length >= 2) {
    return (
      <p className={"text-[15px] md:text-base leading-relaxed text-foreground/80 " + (isBullet ? "pl-4" : "")}>
        {m[1]}
        <span className="font-semibold text-foreground">{m[2]}</span> — {m[3]}
      </p>
    );
  }
  if (isBullet) {
    return <p className="text-[15px] md:text-base leading-relaxed text-foreground/80 pl-4">{line}</p>;
  }
  return <p className="text-[15px] md:text-base leading-relaxed text-foreground/80">{line}</p>;
}

function Paragraphs({ content }) {
  return (
    <div className="space-y-4">
      {content.split("\n").map((line, i) => (line.trim() ? <ContentLine key={i} line={line} /> : null))}
    </div>
  );
}

function ListItems({ items }) {
  return (
    <div className="grid sm:grid-cols-2 gap-px bg-border mt-10 border border-border">
      {items.map((item, i) => (
        <motion.div
          key={i}
          variants={reveal}
          custom={i}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="bg-background p-7 hover-lift"
          data-testid="list-item"
        >
          <div className="flex items-baseline justify-between gap-4">
            <h4 className="font-display text-xl md:text-2xl text-foreground">{item.title}</h4>
          </div>
          {item.meta && (
            <p className="overline text-muted-foreground mt-2 text-[0.62rem]">{item.meta}</p>
          )}
          <p className="text-sm leading-relaxed text-foreground/70 mt-3">{item.description}</p>
        </motion.div>
      ))}
    </div>
  );
}

export default function SectionBlock({ section, index, children }) {
  const flip = index % 2 === 1;
  const hasImage = !!section.image_url;
  const isList = section.type === "list";
  const isCode = section.type === "code";

  return (
    <section
      id={section.key}
      className="scroll-mt-24 px-6 sm:px-10 lg:px-16 py-20 md:py-28 border-b border-border"
      data-testid={`section-${section.key}`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header row */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="flex items-start gap-5 mb-12"
        >
          <span className="font-display text-2xl md:text-3xl text-accent leading-none pt-1">
            {section.number}
          </span>
          <div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95] text-foreground">
              {section.title}
            </h2>
          </div>
        </motion.div>

        {/* Body */}
        {isList ? (
          <>
            {section.content && (
              <motion.p
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="text-lg leading-relaxed text-foreground/75 max-w-2xl"
              >
                {section.content}
              </motion.p>
            )}
            <ListItems items={section.items || []} />
          </>
        ) : (
          <div className={"grid lg:grid-cols-12 gap-10 lg:gap-16 items-start"}>
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className={
                (flip ? "lg:order-2 " : "") +
                (hasImage ? "lg:col-span-7" : "lg:col-span-9")
              }
            >
              {(section.wifi_network || section.wifi_password) && (
                <div className="mb-8 grid sm:grid-cols-2 gap-px bg-border border border-border" data-testid="wifi-block">
                  <div className="bg-background p-6">
                    <p className="subhead text-accent text-sm mb-2">Network</p>
                    <p className="font-display text-2xl md:text-3xl text-foreground break-all" data-testid="wifi-network-value">
                      {section.wifi_network || "—"}
                    </p>
                  </div>
                  <div className="bg-background p-6">
                    <p className="subhead text-accent text-sm mb-2">Password</p>
                    <p className="font-display text-2xl md:text-3xl text-foreground break-all" data-testid="wifi-password-value">
                      {section.wifi_password || "—"}
                    </p>
                  </div>
                </div>
              )}
              {isCode && section.highlight && (
                <div className="mb-8 border-l-2 border-accent pl-5 py-2">
                  <p className="subhead text-accent text-sm mb-2">{section.subtitle || "Details"}</p>
                  <p className="font-display text-4xl md:text-5xl tracking-[0.15em] text-foreground">
                    {section.highlight}
                  </p>
                </div>
              )}
              <Paragraphs content={section.content} />
            </motion.div>

            {hasImage && (
              <motion.div
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                className={"lg:col-span-5 " + (flip ? "lg:order-1" : "")}
              >
                <div className="overflow-hidden clip-frame">
                  <img
                    src={section.image_url}
                    alt={section.title}
                    className="w-full aspect-[3/4] object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </motion.div>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}
